import Paper from '@base/Paper/Paper';
import styled from '@emotion/styled';
import React, { FC, useCallback, useContext, useEffect } from 'react';
import ChevronLeft from '@icon/ChevronLeft';
import { mediaQuery } from '@styles/index';
import { DoubtsAIContext } from '@context/DoubtsAIContext';
import getInitialUnacademyAiConfig from '@api/fetchers/doubts-and-solutions/ai-config';
import { segment } from '@actions/analytics';
import { DOW2_DAILY_LIMIT_REACHED } from '@constants/segment';
import useGoalEventProps from '@hooks/useGoalEventProps';
import InputBoxContainer from './InputBoxContainer';
import Chats from './ChatContainer';
import LimitReachedFooter from './LimitReached';
import LeaveChatConfirmation from './LeaveChatConfirmation';
import Loader from './Loader';
import QuestionOptions from './QuestionOptions';
import ErrorToast from './ErrorToast';
import { UiFlowType } from './types';
import useDoubtSolution from './hooks/useDoubtSolution';
import useImageAiSolution from './hooks/useImageAiSolution';

const Container = styled.div`
  grid-column: span 24;
  margin-top: -40px;

  ${mediaQuery.uptoMobile} {
    background-color: var(--color-base-1);
    margin: 0px;
    height: calc(100vh - 40px - 48px);
  }
`;

const BackButton = styled.div`
  cursor: pointer;
  height: 40px;
  width: 40px;
  margin: 8px 0px;
  display: flex;
  justify-content: center;
  align-items: center;

  ${mediaQuery.uptoMobile} {
    margin: 0px;
  }
`;

const StyledBackIcon = styled(ChevronLeft)`
  width: 24px;
  height: 24px;
`;

const StyledPaper = styled(Paper)`
  padding-bottom: 0;
  height: calc(100vh - 40px - 72px - 16px);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border-radius: 0;
  border: 1px solid var(--color-divider);

  ${mediaQuery.uptoMobile} {
    padding: 0px;
    height: calc(100vh - 40px);
  }
`;

const FooterContainer = styled(Paper)`
  position: sticky;
  bottom: -20px;
  left: 0;
  width: 100%;
  border-radius: 8px 8px 0px 0px;
  display: flex;
  flex-direction: column;
  padding: 16px;
  background-color: var(--color-base-light-0);
  border: 1px solid var(--color-divider);
  border-width: 1px 1px 0px 1px;
`;

const ChatContainer = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: hidden;
`;

const renderHeaderComponent = (headerType: UiFlowType) => {
  switch (headerType) {
    case 'input':
      return null;
    case 'questionOptions':
      return null;
    case 'limit':
      return <ErrorToast message="Daily limit for Free doubts reached" />;
    case 'loading':
      return null;
    case 'error':
      return <ErrorToast message="Something went wrong" />;
    default:
      return null;
  }
};

const RenderModals = ({ goalUid }: { goalUid: string }) => {
  return (
    <>
      <LeaveChatConfirmation type="go back" goalUid={goalUid} />
      <LeaveChatConfirmation type="ask new question" goalUid={goalUid} />
    </>
  );
};

interface IUnacademyAIContainerProps {
  goalUID: string;
  goalSlug: string;
}

const UnacademyAIContainer: FC<IUnacademyAIContainerProps> = ({
  goalUID,
  goalSlug
}) => {
  const {
    flowType,
    setIsGoingBack,
    doubtsUID,
    setFlowType,
    setLimitReachedData,
    setDoubtNumber
  } = useContext(DoubtsAIContext);

  const routeGoalSegmentData = useGoalEventProps({
    goalUID
  });

  const { setPolling } = useDoubtSolution({
    goalUID,
    doubtUID: doubtsUID
  });

  const handleGoBack = useCallback(() => {
    setIsGoingBack(true);
  }, [setIsGoingBack]);

  const renderFooterComponent = useCallback(
    (footerType: UiFlowType) => {
      switch (footerType) {
        case 'input':
          return (
            <InputBoxContainer goalUID={goalUID} setPolling={setPolling} />
          );
        case 'questionOptions':
          return <QuestionOptions />;
        case 'limit':
          return <LimitReachedFooter goalUid={goalUID} goalSlug={goalSlug} />;
        case 'loading':
          return (
            <InputBoxContainer
              goalUID={goalUID}
              loading
              setPolling={setPolling}
            />
          );
        case 'error':
          return (
            <InputBoxContainer goalUID={goalUID} setPolling={setPolling} />
          );
        default:
          return null;
      }
    },
    [goalSlug, goalUID, setPolling]
  );

  useEffect(() => {
    const onPopState = (event: PopStateEvent) => {
      event.preventDefault();
      handleGoBack();
    };
    window.addEventListener('popstate', onPopState);
    return () => {
      window.removeEventListener('popstate', onPopState);
    };
  }, [handleGoBack]);

  useEffect(() => {
    getInitialUnacademyAiConfig(goalUID)
      .then((res) => {
        if ('message' in res) {
          setFlowType('error');
        } else {
          const { daily_limit_exceeded, sub_cta_details, doubt_number } = res;
          setDoubtNumber(doubt_number + 1);
          if (daily_limit_exceeded) {
            setFlowType('limit');
            if (sub_cta_details)
              setLimitReachedData({
                text: sub_cta_details.text,
                cta_text: sub_cta_details.cta_text
              });
            segment.track(DOW2_DAILY_LIMIT_REACHED, {
              doubt_type: 'Doubts AI'
            });
          } else {
            setFlowType('input');
          }
        }
      })
      .catch((e) => {
        setFlowType('error');
      });
  }, [goalUID, setFlowType, setDoubtNumber, setLimitReachedData]);

  useImageAiSolution({ goalUID });
  return (
    <Container>
      <BackButton onClick={handleGoBack}>
        <StyledBackIcon />
      </BackButton>
      <>{renderHeaderComponent(flowType)}</>
      <StyledPaper>
        <RenderModals goalUid={goalUID} />
        <ChatContainer>
          <Chats goalUID={goalUID} />
        </ChatContainer>
        {flowType === 'loading' && <Loader />}
        <FooterContainer>{renderFooterComponent(flowType)}</FooterContainer>
      </StyledPaper>
    </Container>
  );
};

const UnacademyAIContainerWrapper = ({ goalUID, goalSlug }) => {
  return <UnacademyAIContainer goalUID={goalUID} goalSlug={goalSlug} />;
};

export default UnacademyAIContainerWrapper;
